import axios from 'axios';
import { useEffect, useState } from 'react';

interface SettingItem {
  upi_id: string;
  account_number: string;
  bank_name: string;
  ifsc_code: string;
  aff_amount: string;
  account_holder_name: string;
  qr_img: string; // This will store the image URL or path from the server
  invitee_amount: string;
}

const Settings = () => {
  const [settings, setSettings] = useState<SettingItem>({
    upi_id: '',
    account_number: '',
    bank_name: '',
    ifsc_code: '',
    aff_amount: '',
    account_holder_name: '',
    qr_img: '',
    invitee_amount: '',
  });
  const [qrFile, setQrFile] = useState<File | null>(null); // To store the uploaded file
  const [loading, setLoading] = useState<boolean>(true);

  async function fetchSetting() {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL_API}/getPaymentSetting`);
      console.log('Fetched settings:', response.data);

      if (response?.data && response?.data.length > 0 && response?.data[0]) {
        const data = response.data[0];
        setSettings({
          upi_id: data.upi_id || '',
          account_number: data.account_number || '',
          bank_name: data.bank_name || '',
          ifsc_code: data.ifsc_code || '',
          aff_amount: data.aff_amount || '0',
          account_holder_name: data.account_holder_name || '',
          qr_img: data.qr_img || '',
          invitee_amount: data.invitee_amount || '0',
        });
        console.log('Settings loaded:', {
          aff_amount: data.aff_amount,
          invitee_amount: data.invitee_amount
        });
      } else {
        console.warn('No settings found in database. Using defaults.');
        // Keep default values (0) if no data exists
        setSettings({
          upi_id: '',
          account_number: '',
          bank_name: '',
          ifsc_code: '',
          aff_amount: '0',
          account_holder_name: '',
          qr_img: '',
          invitee_amount: '0',
        });
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
      alert('Failed to load settings. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  }

  // Fetch settings data on load
  useEffect(() => {
    fetchSetting();
  }, []);

  // Handle text input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSettings((prevSettings) => ({
      ...prevSettings,
      [name]: value,
    }));
  };

  // Handle file input change
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setQrFile(e.target.files[0]); // Set the file to state
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    console.log('Submitting settings:', {
      aff_amount: settings?.aff_amount,
      invitee_amount: settings?.invitee_amount
    });

    // Create FormData to send both text data and file
    const formData = new FormData();
    formData.append('upi_id', settings?.upi_id || '');
    formData.append('account_number', settings?.account_number || '');
    formData.append('bank_name', settings?.bank_name || '');
    formData.append('ifsc_code', settings?.ifsc_code || '');
    formData.append('account_holder_name', settings?.account_holder_name || '');
    formData.append('aff_amount', settings?.aff_amount || '0');
    formData.append('invitee_amount', settings?.invitee_amount || '0');
    if (qrFile) formData.append('qr_img', qrFile); // Only append if file exists

    console.log('FormData values:', {
      aff_amount: formData.get('aff_amount'),
      invitee_amount: formData.get('invitee_amount')
    });

    try {
      const response = await axios.post(`${process.env.REACT_APP_BASE_URL_API}/updatePaymentSetting`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': 'Bearer ' + localStorage.getItem('token') // ✅ Add JWT Token
        },
      });

      console.log('Update response:', response.data);
      alert('Settings updated successfully!');

      // Refresh the data from server to ensure UI is in sync
      await fetchSetting();
    } catch (error) {
      console.error("Error updating settings:", error);
      alert('Failed to update settings. Please try again.');
    }
  };

  return (
    <div className='ml-0  p-4'>
      <h2 className='text-2xl font-semibold mb-4 text-black'>Affiliate Settings</h2>

      {loading ? (
        <div className='flex items-center justify-center py-12'>
          <div className='text-center'>
            <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4'></div>
            <p className='text-gray-600'>Loading settings...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Current Settings Summary */}
          <div className='bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6 mb-6'>
            <h3 className='text-lg font-semibold text-gray-800 mb-4 flex items-center'>
              <span className='mr-2'>📊</span>
              Current Affiliate Settings
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div className='bg-white rounded-lg p-4 shadow-sm'>
                <p className='text-sm text-gray-600 mb-1'>Affiliate Commission</p>
                <p className='text-3xl font-bold text-blue-600'>
                  {settings?.aff_amount || '0'}%
                </p>
                <p className='text-xs text-gray-500 mt-1'>
                  Commission on deposits for referrers
                </p>
              </div>
              <div className='bg-white rounded-lg p-4 shadow-sm'>
                <p className='text-sm text-gray-600 mb-1'>New User Signup Bonus</p>
                <p className='text-3xl font-bold text-green-600'>
                  ৳{settings?.invitee_amount || '0'}
                </p>
                <p className='text-xs text-gray-500 mt-1'>
                  Instant bonus for new signups
                </p>
              </div>
            </div>
            <div className='mt-4 bg-white rounded-lg p-4 shadow-sm'>
              <p className='text-sm font-semibold text-gray-700 mb-2'>💡 How it works:</p>
              <ul className='text-xs text-gray-600 space-y-1'>
                <li>• New users get ৳{settings?.invitee_amount || '0'} instantly when they sign up via referral link</li>
                <li>• Referrers earn {settings?.aff_amount || '0'}% commission when their referred users deposit money</li>
                <li>• Example: If a referred user deposits ৳1000, referrer gets ৳{((parseFloat(settings?.aff_amount || '0') / 100) * 1000).toFixed(2)}</li>
              </ul>
            </div>
          </div>

          <hr className='my-6' />
          <h2 className='text-2xl font-semibold mb-4 text-black'>Update Affiliate Settings</h2>
          <p className='text-sm text-gray-600 mb-4'>
            Configure referral bonuses and commission rates for your platform.
          </p>
          <form onSubmit={handleSubmit} className='space-y-4 bg-white p-6 rounded-lg shadow-sm border'>
            <div>
              <label className="text-black font-semibold">Affiliate Commission (%)</label>
              <p className='text-xs text-gray-500 mb-2'>
                Percentage of each deposit that will be credited to the referrer when their referred users deposit (e.g., 25 = 25%)
              </p>
              <input
                type='number'
                name='aff_amount'
                value={settings?.aff_amount}
                onChange={handleChange}
                className='border border-gray-300 rounded p-3 w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                placeholder='Enter percentage (e.g., 25 for 25%)'
                min='0'
                max='100'
                step='0.01'
              />
              <p className='text-xs text-blue-600 mt-1'>
                Current: {settings?.aff_amount || '0'}% commission
              </p>
            </div>
            <div>
              <label className="text-black font-semibold">New User Signup Bonus (৳)</label>
              <p className='text-xs text-gray-500 mb-2'>
                Fixed amount credited to NEW USERS when they sign up using a referral link (instant bonus)
              </p>
              <input
                type='number'
                name='invitee_amount'
                value={settings?.invitee_amount}
                onChange={handleChange}
                className='border border-gray-300 rounded p-3 w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                placeholder='Enter amount (e.g., 50)'
                min='0'
                step='0.01'
              />
              <p className='text-xs text-green-600 mt-1'>
                Current: ৳{settings?.invitee_amount || '0'} signup bonus
              </p>
            </div>
            <button type='submit' className='bg-blue-500 hover:bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold transition-colors duration-200 w-full'>
              💾 Save Settings
            </button>
          </form>
        </>
      )}
    </div>
  );
};

export default Settings;
